import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import sessionManager from "../utils/simpleSessionManager";
import posthog from "./posthog";
import { identifyNormalUser } from "./identity";
import { track } from "./track";
import { EVENTS } from "./events";
import { setAdminLoginData } from "../actions/login/login";
import {
  setAdminHeaderMenuData,
  setChecklinkHeaderMenuData,
} from "../actions/headerMenu/headerMenu";

// Resolves bug #1750 — sessionManager.exitImpersonation() previously had no caller
// anywhere in the app, so there was no way to end an impersonation session cleanly.
export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isImpersonating, setIsImpersonating] = useState(
    sessionManager.getSessionType() === "impersonation"
  );
  const [elapsedLabel, setElapsedLabel] = useState("");

  useEffect(() => {
    const tick = () => {
      const active = sessionManager.getSessionType() === "impersonation";
      setIsImpersonating(active);
      if (!active) return;

      const session = sessionManager.getCurrentSession();
      if (session && session.impersonationTime) {
        const startedMs = new Date(session.impersonationTime).getTime();
        const minutes = Math.max(
          0,
          Math.round((Date.now() - startedMs) / 60000)
        );
        setElapsedLabel(
          minutes < 1 ? t("just now") : t("{{minutes}}m ago", { minutes })
        );
      }
    };

    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [t]);

  if (!isImpersonating) return null;

  const session = sessionManager.getCurrentSession();
  const target = (session && session.loginData) || {};

  const handleExit = () => {
    const startedMs =
      session && session.impersonationTime
        ? new Date(session.impersonationTime).getTime()
        : Date.now();

    track(EVENTS.IMPERSONATION_ENDED, {
      end_reason: "exited",
      duration_seconds: Math.round((Date.now() - startedMs) / 1000),
    });

    const success = sessionManager.exitImpersonation();
    if (!success) return;

    posthog.reset();
    const adminSession = sessionManager.getAdminSession();
    if (adminSession && adminSession.adminData) {
      identifyNormalUser(adminSession.adminData);
      dispatch(setAdminLoginData({ responseData: adminSession.adminData }));
      dispatch(setAdminHeaderMenuData(adminSession.headerMenuData));
      dispatch(setChecklinkHeaderMenuData("Admin"));
    }

    navigate("/dashboard");
  };

  return (
    <div
      role="alert"
      style={{
        width: "100%",
        background: "#fa8c16",
        color: "#fff",
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: 14,
        position: "relative",
        zIndex: 1000,
      }}
    >
      <span>
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#fff",
            marginRight: 8,
          }}
        />
        {t("You are impersonating")} <strong>{target.dealerCompany}</strong>{" "}
        {t("(Vendor #{{dealerId}})", { dealerId: target.dealerId })} ·{" "}
        {t("started")} {elapsedLabel} · {t("all actions are recorded")}
      </span>
      <button
        type="button"
        onClick={handleExit}
        style={{
          background: "#fff",
          color: "#fa8c16",
          border: "none",
          borderRadius: 4,
          padding: "4px 12px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {t("Exit impersonation")}
      </button>
    </div>
  );
}
